<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import StatusChip from "./StatusChip.vue";
import { gateReviewView } from "../composables/gateReview";
import { t } from "../i18n";

export interface GateDecisionView {
  id: string;
  mode: string;
  verdict: "approve" | "rewrite" | "block";
  reasons: string[];
  hits: Array<{ rule: string; detail: string }>;
  rewrittenText: string | null;
  canOverride: boolean;
}
const props = defineProps<{
  open: boolean;
  decision: GateDecisionView | null;
  draft: string;
  busy?: boolean;
}>();
const emit = defineEmits<{
  close: [];
  accept: [];
  edit: [text: string];
  override: [];
}>();
const edited = ref("");
watch(
  () => props.open,
  (v) => {
    if (v) edited.value = props.decision?.rewrittenText ?? props.draft;
  },
);
// An edit reviewed again comes back as a new decision while the window stays open.
watch(
  () => props.decision?.id,
  () => {
    if (props.open) edited.value = props.decision?.rewrittenText ?? props.draft;
  },
);
const blocked = computed(() => props.decision?.verdict === "block");
const view = computed(() =>
  props.decision ? gateReviewView(props.decision) : null,
);
const rules = computed(() =>
  (props.decision?.hits ?? []).map(
    (h) => `${t(("gate.rule." + h.rule) as never)}: ${h.detail}`,
  ),
);
/** The judge's own reasons; deterministic hits are already listed above in translated form. */
const judgeReasons = computed(() => {
  const raw = new Set(
    (props.decision?.hits ?? []).map((h) => `${h.rule}:${h.detail}`),
  );
  return (props.decision?.reasons ?? []).filter((r) => !raw.has(r));
});
</script>
<template>
  <Modal :open="open" :title="t('gate.title')" wide @close="emit('close')">
    <div v-if="decision" class="modal-form">
      <p class="small muted">{{ t("gate.hint") }}</p>
      <div class="row" style="gap: 8px">
        <StatusChip
          :status="blocked ? 'failed' : 'waiting_contact'"
          :label="
            t(('gate.verdict.' + (blocked ? 'block' : 'rewrite')) as never)
          "
        />
      </div>
      <div class="gate-draft">
        <span class="sg-demo-label">{{ t("gate.draft") }}</span>
        <p>{{ draft }}</p>
      </div>
      <div v-if="rules.length || decision.reasons.length" class="gate-reasons">
        <span class="sg-demo-label">{{ t("gate.reasons") }}</span>
        <ul>
          <li v-for="r in rules" :key="'r' + r">{{ r }}</li>
          <li v-for="r in judgeReasons" :key="'j' + r">{{ r }}</li>
        </ul>
      </div>
      <label class="wa-field"
        ><span>{{ t(view!.field) }}</span
        ><textarea placeholder=" " v-model="edited" rows="4" />
      </label>
      <p v-if="view?.blockedHint" class="small danger">
        {{ t("gate.blocked_hint") }}
      </p>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        v-if="view?.override"
        variant="ghost"
        :loading="busy"
        @click="emit('override')"
        >{{ t("gate.override") }}</Button
      >
      <Button
        v-if="decision?.rewrittenText && edited === decision.rewrittenText"
        :loading="busy"
        @click="emit('accept')"
        >{{ t("gate.accept") }}</Button
      >
      <Button
        v-else
        :loading="busy"
        :disabled="!edited.trim() || edited === draft"
        @click="emit('edit', edited.trim())"
        >{{ t("gate.edit") }}</Button
      >
    </template>
  </Modal>
</template>
